/**
 * Hinweise, die ein Paket vor dem Einreichen sinnvoll machen.
 *
 * Der häufigste Fehler im Katalog: Ein Paket bekommt einen Drucker, die
 * Anzahl der Ausdrucke schreibt der Vermieter in den Paketnamen — und das
 * Fotodruck-Extra legt er nie an. Im Warenkorb steht dann „Requisiten", aber
 * nicht „150 Ausdrucke", obwohl genau das der Grund für den Preis ist.
 *
 * Gemessen am 2026-08-23: 18 von 126 Paketen mit Drucker hatten keine
 * Druckangabe. Umgekehrt hatte kein einziges Paket ohne Drucker eine — der
 * Fehler geht also nur in eine Richtung, und die Namen verraten ihn:
 * „print 400", „Sofortdruck", „FOTOBOX MIT DRUCKFLATRATE", „Druckerpaket 2
 * mit 150 gedruckten Bilden".
 *
 * Das ist der Ansatz: Der Vermieter hat die Antwort schon gegeben, nur im
 * falschen Feld. Also lesen wir sie dort und bieten an, sie zu übernehmen.
 */
import { abgeleiteterName } from "./partnerModel.js";

// Die Schwelle für „unbegrenzt" liegt im Projekt bei 999 — partnerStore.js
// wertet alles ab 999 als Flatrate. Ein eigener Wert hier hätte eine
// Flatrate erzeugt, die nirgends als solche erkannt wird.
import { UNLIMITED_PRINTS } from "./partnerModel.js";

/**
 * Liest eine Druckangabe aus einem Paketnamen.
 *
 * Bewusst nur die eindeutigen Fälle: eine Zahl direkt vor einem Druckwort,
 * oder ein Flatrate-Wort. Eine Zahl allein („Paket 2") ist keine Druckangabe,
 * und ein falscher Vorschlag ist schlimmer als keiner — er wandert per Klick
 * in die Daten.
 */
export function druckAngabeAusName(name = "") {
  const text = String(name);

  // Ohne Wortgrenze davor: „DRUCKFLATRATE" ist ein zusammengesetztes Wort,
  // und genau so schreiben es Vermieter.
  // „Druckflat" steht in fünf Fetebox-Paketen und meint dasselbe — die
  // Kurzform ohne „-rate" ist im Sprachgebrauch mindestens so verbreitet.
  if (/(flat ?rate|druckflat|unbegrenzt|unlimited)/i.test(text)) {
    return { flatrate: true };
  }

  const zahl = /(\d{2,4})\s*(?:x\s*)?(gedruckte|ausdruck|abzüg|bilder|fotos|prints?|drucke)/i.exec(
    text
  );
  if (zahl) return { anzahl: Number(zahl[1]) };

  // Auch die umgekehrte Reihenfolge: „print 400", „Druck 200"
  const umgekehrt = /\b(?:print|druck|fotodruck)\w*\s+(\d{2,4})\b/i.exec(text);
  if (umgekehrt) return { anzahl: Number(umgekehrt[1]) };

  return null;
}

/**
 * Fehlt diesem Paket die Druckangabe?
 *
 * Gibt null zurück, wenn alles in Ordnung ist. Sonst eine Beschreibung des
 * Problems und — wenn der Name sie verrät — einen übernehmbaren Vorschlag.
 */
export function fehlendeDruckangabe({ paket, extras }) {
  if (!paket?.printerId) return null;

  const enthalten = (paket.extraIds || [])
    .map((id) => (extras || []).find((e) => e.id === id))
    .filter(Boolean);
  if (enthalten.some((e) => e.category === "Fotodruck")) return null;

  return {
    grund:
      "Das Paket hat einen Drucker, aber keine Angabe zu den Ausdrucken. " +
      "Im Warenkorb sieht der Kunde dann nicht, wie viele Fotos enthalten sind.",
    vorschlag: druckAngabeAusName(paket.name),
  };
}

/** Wie das vorgeschlagene Extra heißen und aussehen soll. */
export function extraAusVorschlag(vorschlag) {
  if (!vorschlag) return null;
  if (vorschlag.flatrate) {
    return {
      category: "Fotodruck",
      name: "Druck-Flatrate",
      prints: UNLIMITED_PRINTS,
      isPrintFlatrate: true,
    };
  }
  return {
    category: "Fotodruck",
    name: `${vorschlag.anzahl} Ausdrucke`,
    prints: vorschlag.anzahl,
    isPrintFlatrate: false,
  };
}

/**
 * Wie das Extra im Paket heißt.
 *
 * Viele Vermieter benennen ihre Extras nach der Sache selbst: „Fotodruck-
 * Flatrate", „106 Ausdrucke". Steht daneben noch die Auszeichnung, liest der
 * Kunde dieselbe Angabe zweimal. Also raus aus dem Namen, wo die Auszeichnung
 * sie ohnehin zeigt.
 *
 * Und eine Flatrate hat keine Anzahl. Aus der Migration tragen 19 Extras eine
 * im Namen — meist die 999, mit der Bubble „unbegrenzt" ausgedrückt hat. Auf
 * der Seite stand dann „999 Ausdrucke Flatrate": eine Zahl, die nichts
 * bedeutet, neben einem Wort, das ihr widerspricht.
 */
export function anzeigeName(extra) {
  const name = String(extra?.name || "").trim();

  /**
   * Extras, deren Name sich aus ihren Angaben ergibt.
   *
   * Requisiten und Stundenpakete haben im Backend kein Namensfeld mehr — was
   * dort steht, ergibt sich aus Kategorie und Stückzahl. Bei neu angelegten
   * Extras ist der Name deshalb leer, und im Vergleich stand dann nichts:
   * ein Häkchen, ein Leerzeichen und, falls ein Bild dabei war, allein der
   * Link „Bild ansehen".
   *
   * Ein eigener Name bleibt aber stehen. Zehn Anbieter haben einen vergeben —
   * „Mister Mory`s Requisitenkiste", „Nuniprops & Accessoires Kit",
   * „Digitale Requisiten" —, und der sagt mehr als das Wort der Kategorie.
   * Ersetzt wird nur, was ohnehin nur die Kategorie wiederholt.
   */
  if (extra?.category === "Requisiten" || extra?.category === "Stundenpaket") {
    if (!name || name === extra.category) return abgeleiteterName(extra);
    return name;
  }

  if (!extra?.isPrintFlatrate) return name;

  // Bleibt nach der Zahl nichts übrig, hieß das Extra nur „999 Ausdrucke".
  // Dann ist „Fotodruck" die Antwort — nicht der Name zurück, den wir gerade
  // als bedeutungslos erkannt haben.
  const ohneAnzahl = name.replace(/^\s*\d+\s*Ausdrucke?\b/i, "").trim();
  const basis = ohneAnzahl || "Fotodruck";

  return basis.replace(/[-\s]*Flatrate\s*$/i, "").trim() || "Fotodruck";
}
